// 💾 src/components/trade/PairList.tsx
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import "./style/PairList.scss"

type Ticker = {
  symbol: string
  lastPrice: string
  priceChangePercent: string
}

type Props = {
  selectedPair?: string
}

const PairList = ({ selectedPair }: Props) => {
  const [tickers, setTickers] = useState<Ticker[]>([])
  const [coinIcons, setCoinIcons] = useState<Record<string, string>>({})
  const [coinNames, setCoinNames] = useState<Record<string, string>>({})
  const [topSymbols, setTopSymbols] = useState<string[]>([])

  const { lng = "en" } = useParams()
  const navigate = useNavigate()

  // 1️⃣ Load icon and name map once
  useEffect(() => {
    const fetchCoinMeta = async () => {
      try {
        const res = await fetch(
          "https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=100&page=1"
        )
        const coins = await res.json()

        const iconMap: Record<string, string> = {}
        const nameMap: Record<string, string> = {}
        const symbols: string[] = []

        coins.forEach((c: any) => {
          const s = c.symbol.toUpperCase()
          symbols.push(s)
          iconMap[s] = c.image
          nameMap[s] = c.name
        })

        setTopSymbols(symbols)
        setCoinIcons(iconMap)
        setCoinNames(nameMap)
      } catch (err) {
        console.error("Failed to fetch CoinGecko meta", err)
      }
    }

    fetchCoinMeta()
  }, [])

  // 2️⃣ Load Binance prices every 3 seconds
  useEffect(() => {
    if (topSymbols.length === 0) return

    const fetchTicker = async () => {
      try {
        const res = await fetch("https://api.binance.com/api/v3/ticker/24hr")
        const data = await res.json()

        const filtered = data
          .filter((item: any) => {
            const base = item.symbol.replace("USDT", "")
            return (
              item.symbol.endsWith("USDT") &&
              !item.symbol.includes("DOWN") &&
              !item.symbol.includes("UP") &&
              topSymbols.includes(base)
            )
          })
          .slice(0, 50)
          .map((item: any) => ({
            symbol: item.symbol,
            lastPrice: item.lastPrice,
            priceChangePercent: item.priceChangePercent
          }))

        setTickers(filtered)
      } catch (err) {
        console.error("Failed to fetch Binance ticker", err)
      }
    }

    fetchTicker()
    const interval = setInterval(fetchTicker, 3000)
    return () => clearInterval(interval)
  }, [topSymbols])

  const handleSelect = (binanceSymbol: string) => {
    const formatted = binanceSymbol.replace("USDT", "_USDT")
    navigate(`/${lng}/exchange/${formatted}`)
  }

  const getDisplaySymbol = (raw: string) => {
    const base = raw.replace("USDT", "")
    return `${base}/USDT`
  }

  return (
    <div className="pair-list-wrapper">
      <ul>
        {tickers.map(t => {
          const baseSymbol = t.symbol.replace("USDT", "")
          const formattedSymbol = t.symbol.replace("USDT", "_USDT")
          const isActive = formattedSymbol === selectedPair
          const isUp = parseFloat(t.priceChangePercent) >= 0

          return (
            <li
              key={t.symbol}
              className={`pair-item ${isActive ? "active" : ""}`}
              onClick={() => handleSelect(t.symbol)}
            >
              <div className="left">
                <img
                  src={coinIcons[baseSymbol] || "/default-coin.png"}
                  alt={baseSymbol}
                  className="icon"
                />
                <div className="symbol-labels">
                  <span className="symbol">{getDisplaySymbol(t.symbol)}</span>
                  <span className="coin-name">{coinNames[baseSymbol]}</span>
                </div>
              </div>

              <div className="right">
                <div className="price">${parseFloat(t.lastPrice).toFixed(4)}</div>
                <div className={`change ${isUp ? "up" : "down"}`}>
                  {isUp ? "▲" : "▼"} {parseFloat(t.priceChangePercent).toFixed(2)}%
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default PairList
